import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, FileText, Calendar, CreditCard, User, AlertCircle, ArrowUpRight, ArrowDownLeft, CheckCircle2 as BadgeCheck, Edit2, Trash2, GraduationCap, Bus as BusIcon, MapPin, Wallet, BadgeCheck as BadgeCheckIcon, Printer, FileSpreadsheet, BarChart2 } from 'lucide-react';
import { exportToExcel } from '../../utils/excelExport';
import { Card, Button, Input, Select, Badge } from '../../components/ui/index.jsx';

import { Modal } from '../../components/ui/Modal';
import { useGet } from '../../hooks/useApi';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';


const PaymentList = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
    const [isBreakdownModalOpen, setIsBreakdownModalOpen] = useState(false);
    const [breakdownData, setBreakdownData] = useState(null);
    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [isUnpaidModalOpen, setIsUnpaidModalOpen] = useState(false);
    const [isBeneficiaryStatsModalOpen, setIsBeneficiaryStatsModalOpen] = useState(false);
    const today = new Date().toISOString().split('T')[0];
    const [printDateFrom, setPrintDateFrom] = useState(today);
    const [printDateTo, setPrintDateTo] = useState(today);
    const [printLoading, setPrintLoading] = useState(false);
    const [statsMonths, setStatsMonths] = useState([]);
    const [statsData, setStatsData] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const { hasPermission, selectedYear } = useAuth();
    const canCreate = hasPermission('payments', 'create');
    const { success, error: toastError, info } = useToast();

    const handleOpenBreakdownModal = async () => {
        setIsBreakdownModalOpen(true);
        setBreakdownLoading(true);
        try {
            const queryParams = new URLSearchParams();
            if (selectedYear?.id) queryParams.append('academic_year_id', selectedYear.id);
            if (selectedPaymentMode) queryParams.append('payment_mode_id', selectedPaymentMode);
            if (searchTerm) queryParams.append('search', searchTerm);
            const res = await api.get(`/reports/revenue-breakdown?${queryParams.toString()}`);
            setBreakdownData(res.data);
        } catch (err) {
            toastError('خطأ أثناء تحميل تفاصيل المداخيل');
        } finally {
            setBreakdownLoading(false);
        }
    };

    const activeMonths = statsMonths.length > 0 ? statsMonths : (selectedMonth ? [selectedMonth] : []);
    const monthFilterQuery = activeMonths.length > 0 ? `&months=${encodeURIComponent(activeMonths.join(','))}` : '';
    const yearFilterQuery = selectedYear?.id ? `&academic_year_id=${selectedYear.id}` : '';
    const { data: paymentsResponse, loading, refresh } = useGet(`/payments?page=${currentPage}&search=${searchTerm}${selectedPaymentMode ? `&payment_mode_id=${selectedPaymentMode}` : ''}${monthFilterQuery}${yearFilterQuery}`);
    const { data: academicYearsRaw } = useGet('/academic-years');
    const { data: paymentModesRaw } = useGet('/payment-modes');

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذه العملية؟')) {
            try {
                await api.delete(`/payments/${id}`);
                success('تم حذف العملية بنجاح');
                refresh();
            } catch (err) {
                toastError(err.response?.data?.message || 'خطأ أثناء الحذف');
            }
        }
    };

    const handlePrint = (payment) => {
        localStorage.setItem('print_payment', JSON.stringify(payment));
        window.open('/print/payment-receipt', '_blank');
    };

    // Safely extract arrays (API may return paginated { data: [], meta: {} } or plain [])
    const academicYears = Array.isArray(academicYearsRaw) ? academicYearsRaw : (academicYearsRaw?.data || []);
    const paymentModes = Array.isArray(paymentModesRaw) ? paymentModesRaw : (paymentModesRaw?.data || []);

    const payments = paymentsResponse?.data || [];
    const pagination = paymentsResponse?.meta || null;

    const totalCount = paymentsResponse?.total_count || 0;
    const totalCollected = paymentsResponse?.total_collected || 0;
    const totalPending = paymentsResponse?.total_pending || 0;
    const totalBankDeposited = paymentsResponse?.total_bank_deposited || 0;
    const totalEspeceAmount = paymentsResponse?.total_espece_amount || 0;
    const totalChequeAmount = paymentsResponse?.total_cheque_amount || 0;
    
    // Global totals for absolute visibility
    const globalTotalCount = paymentsResponse?.global_total_count || 0;
    const globalTotalCollected = paymentsResponse?.global_total_collected || 0;
    const globalTotalPending = paymentsResponse?.global_total_pending || 0;
    const globalTotalBankDeposited = paymentsResponse?.global_total_bank_deposited || 0;
    const globalChequeAmount = paymentsResponse?.global_cheque_amount || 0;
    const globalEspeceAmount = paymentsResponse?.global_espece_amount || 0;


    const handlePrintReport = async () => {
        if (!printDateFrom || !printDateTo) {
            toastError('يرجى تحديد تاريخ البداية والنهاية');
            return;
        }
        setPrintLoading(true);
        try {
            const res = await api.get(`/payments?all=true&date_from=${printDateFrom}&date_to=${printDateTo}${yearFilterQuery}`);
            const data = res.data?.data || [];
            if (data.length === 0) {
                info('لا توجد مدفوعات في هذه الفترة');
                return;
            }
            // Sort Oldest to Newest for print
            const sortedPayments = [...data].sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));
            localStorage.setItem('print_payments_report', JSON.stringify({ payments: sortedPayments, date_from: printDateFrom, date_to: printDateTo }));
            window.open('/print/payments-report', '_blank');
            setIsPrintModalOpen(false);
        } catch (err) {
            toastError('خطأ أثناء جلب البيانات');
        } finally {
            setPrintLoading(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            const response = await api.get(`/payments?all=true&search=${searchTerm}${selectedPaymentMode ? `&payment_mode_id=${selectedPaymentMode}` : ''}${monthFilterQuery}${yearFilterQuery}`);
            const paymentsToExport = response.data?.data || response.data || [];
            
            const data = paymentsToExport.map(p => ({
                'التاريخ': p.payment_date,
                'رقم الوصل': p.id,
                'الطالب': p.student?.full_name || '--',
                'المؤسسة': p.student?.institution?.name || '--',
                'المستوى': p.student?.school_class?.name || '--',
                'السنة الدراسية': p.academic_year?.name || '--',
                'المبلغ': p.amount,
                'الأشهر': (p.months || []).join('، '),
                'الوسيلة': p.payment_mode_details?.name || p.payment_mode,
                'الحالة البنكية': p.bank_status === 'bank_confirmed' ? 'تم الايداع البنكي' : (p.bank_status === 'deposited' ? 'تم الايداع' : 'لم يودع بعد'),
                'صاحب العملية': p.user_name || '--'
            }));
            
            exportToExcel(data, `المدفوعات_${new Date().toISOString().split('T')[0]}`);
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl  text-slate-800">إدارة المدفوعات</h2>
                    <p className="text-slate-400 text-[11px] leading-none">تتبع المداخيل ومستحقات النقل المدرسي</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <button 
                        onClick={() => setIsStatsModalOpen(true)}
                        className="bg-white px-4 py-2 rounded-lg border border-slate-100 flex items-center gap-2 hover:bg-slate-50 transition-colors shadow-sm cursor-pointer group"
                    >
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider group-hover:text-primary transition-colors">تفاصيل المداخيل:</span>
                        <Badge variant="primary" className="text-xs">{activeMonths.length > 0 ? `${activeMonths.length} أشهر` : 'عرض الكل'}</Badge>
                    </button>
                    
                    <Button 
                        variant="secondary" 
                        onClick={() => setIsPrintModalOpen(true)} 
                        className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200"
                    >
                        <Printer className="w-4 h-4 ml-2" />
                        طباعة اللائحة
                    </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => setIsBeneficiaryStatsModalOpen(true)} 
                            className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
                        >
                            <BarChart2 className="w-4 h-4 ml-2 text-emerald-600" />
                            إحصائيات المستفيدين
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={() => setIsUnpaidModalOpen(true)} 
                            className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
                        >
                            <AlertCircle className="w-4 h-4 ml-2" />
                             غير المسددين
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={handleExportExcel}
                            className="border-slate-200 text-slate-600"
                        >
                            <FileSpreadsheet className="w-4 h-4 ml-2 text-green-600" />
                         
                    </Button>
                    {canCreate && (
                        <Button onClick={() => { setSelectedPayment(null); setIsModalOpen(true); }} className="shadow-lg shadow-primary/20">
                            <Plus className="w-4 h-4 ml-2" />
                            إضافة دفع
                        </Button>
                    )}
                    
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500">
                        <CreditCard size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">عدد المدفوعات</p>
                        <p className="text-lg font-bold text-slate-700">{totalCount}</p>
                    </div>
                </Card>
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-primary/5 flex items-center justify-center text-primary">
                        <Wallet size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">إجمالي المحصل</p>
                        <p className="text-lg font-bold text-primary">{totalCollected.toLocaleString()} د.م</p>
                    </div>
                </Card>
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                        <FileText size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">عمليات بنكية</p>
                        <p className="text-lg font-bold text-slate-700">{(totalCollected - totalEspeceAmount).toLocaleString()} د.م</p>
                    </div>
                </Card>
                <Card className="p-4 bg-white border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                        <BadgeCheck size={20} />
                    </div>
                    <div>
                        <p className="text-[10px] text-slate-400 uppercase font-bold">المجموع نقداً</p>
                        <p className="text-lg font-bold text-slate-700">{totalEspeceAmount.toLocaleString()} د.م</p>
                    </div>
                </Card>
            </div>

            <Card className="p-4 md:p-6 bg-white/50 backdrop-blur-sm border-slate-100">
                <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="flex-1 w-full">
                        <Input 
                            placeholder="بحث باسم الطالب أو رقم الوصل..." 
                            icon={Search} 
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1); // Reset to first page
                            }}
                        />
                    </div>
                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <Select 
                            options={[
                                { value: '', label: 'الشهر (الكل)' },
                                { value: 'سبتمبر', label: 'سبتمبر' },
                                { value: 'أكتوبر', label: 'أكتوبر' },
                                { value: 'نوفمبر', label: 'نوفمبر' },
                                { value: 'ديسمبر', label: 'ديسمبر' },
                                { value: 'يناير', label: 'يناير' },
                                { value: 'فبراير', label: 'فبراير' },
                                { value: 'مارس', label: 'مارس' },
                                { value: 'أبريل', label: 'أبريل' },
                                { value: 'مايو', label: 'مايو' },
                                { value: 'يونيو', label: 'يونيو' },
                                { value: 'يوليو', label: 'يوليو' },
                                { value: 'أغسطس', label: 'أغسطس' },
                            ]}
                            value={selectedMonth}
                            onChange={(e) => { setSelectedMonth(e.target.value); setCurrentPage(1); }}
                        />
                        <Select 
                            options={[{ value: '', label: 'طريقة الدفع' }, ...(paymentModes?.map(pm => ({ value: pm.id, label: pm.name })) || [])]} 
                            value={selectedPaymentMode}
                            onChange={(e) => { setSelectedPaymentMode(e.target.value); setCurrentPage(1); }}
                        />
                    </div>
                </div>
            </Card>

            <Card className="overflow-hidden border-slate-100">
                <div className="overflow-x-auto">
                    <table className="w-full text-right">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase">التاريخ</th>
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase">الطالب</th>
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase">السنة الدراسية</th>
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase">المبلغ</th>
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase">الوسيلة</th>
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase">صاحب العملية</th>
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase">الحالة</th>
                            <th className="px-6 py-4 text-[11px]  text-slate-400 uppercase text-left w-20">الإجراءات</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading ? (
                            <tr><td colSpan="7" className="py-12 text-center animate-pulse">جاري التحميل...</td></tr>
                        ) : payments?.length === 0 ? (
                            <tr><td colSpan="7" className="py-12 text-center text-slate-400">لا توجد عمليات دفع مسجلة</td></tr>
                        ) : (
                            payments?.map(payment => (
                                <tr key={payment.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm  text-slate-700">{new Date(payment.payment_date).toLocaleDateString('ar-MA')}</span>
                                            <span className="text-[10px] text-slate-400  uppercase tracking-wider">وصل #{payment.id}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-400"><User size={14} /></div>
                                            <span className="text-sm  text-slate-800">{payment.student?.full_name}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-xs  text-slate-500">{payment.academic_year?.name}</td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm  text-slate-800">{payment.amount} د.م</span>
                                            {payment.months?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {payment.months.map(m => (
                                                        <span key={m} className="text-[9px] bg-slate-100 text-slate-500 px-1 rounded-sm">{m}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Badge variant="primary">{payment.payment_mode_details?.name || payment.payment_mode}</Badge>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-400"><User size={10} /></div>
                                            <span className="text-[11px] text-slate-600 font-medium">{payment.user_name || '--'}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {payment.bank_status === 'bank_confirmed' ? (
                                            <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                                                <BadgeCheck size={14} className="text-emerald-500" />
                                                تم ايداع في البنك
                                            </div>
                                        ) : payment.bank_status === 'deposited' ? (
                                            <div className="flex items-center gap-1.5 text-xs text-blue-600 font-medium">
                                                <BadgeCheck size={14} className="text-blue-500" />
                                                تم الايداع
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                                <AlertCircle size={14} className="text-slate-400" />
                                                لم يودع بعد
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-end gap-1">
                                            {payment.bank_status !== 'bank_confirmed' && (
                                                <button 
                                                    onClick={() => { setSelectedPayment(payment); setIsModalOpen(true); }}
                                                    className="p-1 rounded-md text-slate-400 hover:text-primary hover:bg-primary/5 transition-colors"
                                                    title="تعديل"
                                                >
                                                    <Edit2 size={14} />
                                                </button>
                                            )}
                                            <button 
                                                onClick={() => handlePrint(payment)}
                                                className="p-1 rounded-md text-slate-400 hover:text-emerald-500 hover:bg-emerald-50 transition-colors"
                                                title="طباعة الوصل"
                                            >
                                                <Printer size={14} />
                                            </button>
                                            {payment.bank_status !== 'bank_confirmed' && (
                                                <button 
                                                    onClick={() => handleDelete(payment.id)}
                                                    className="p-1 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                                    title="حذف"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>

                {/* Pagination Controls */}
                {pagination && pagination.last_page > 1 && (
                    <div className="px-6 py-4 bg-slate-50/30 border-t border-slate-100 flex items-center justify-between">
                        <div className="text-xs text-slate-500 font-normal">
                            عرض <span className="font-medium text-slate-700">{pagination.from}</span> إلى <span className="font-medium text-slate-700">{pagination.to}</span> من أصل <span className="font-medium text-slate-700">{pagination.total}</span> عملية دفع
                        </div>
                        <div className="flex items-center gap-2">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="h-8 px-3 text-xs"
                            >
                                السابق
                            </Button>
                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                                    let pageNum = i + 1;
                                    if (pagination.last_page > 5 && currentPage > 3) {
                                        pageNum = currentPage - 3 + i + 1;
                                        if (pageNum > pagination.last_page) pageNum = pagination.last_page - (4 - i);
                                    }
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => setCurrentPage(pageNum)}
                                            className={`w-8 h-8 rounded-lg text-xs transition-all ${
                                                currentPage === pageNum 
                                                ? 'bg-primary text-white shadow-sm font-medium' 
                                                : 'text-slate-500 hover:bg-slate-100'
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                })}
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.last_page))}
                                disabled={currentPage === pagination.last_page}
                                className="h-8 px-3 text-xs"
                            >
                                التالي
                            </Button>
                        </div>
                    </div>
                )}
            </Card>

            <Modal 
                isOpen={isModalOpen} 
                onClose={() => { setIsModalOpen(false); setSelectedPayment(null); }} 
                title={selectedPayment ? 'تعديل دفعة' : 'تسجيل عملية دفع جديدة'}
                size="5xl"
            >
                <PaymentForm 
                    years={academicYears} 
                    modes={paymentModes}
                    payment={selectedPayment}
                    onSuccess={() => { setIsModalOpen(false); setSelectedPayment(null); refresh(); }} 
                />
            </Modal>

            {/* Print Date Range Modal */}
            <Modal
                isOpen={isPrintModalOpen}
                onClose={() => setIsPrintModalOpen(false)}
                title="طباعة لائحة المدفوعات"
                size="sm"
            >
                <div className="space-y-5 p-1">
                    <p className="text-xs text-slate-400">حدد الفترة الزمنية لاستخراج لائحة المدفوعات وطباعتها.</p>
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label="من تاريخ"
                            type="date"
                            value={printDateFrom}
                            onChange={e => setPrintDateFrom(e.target.value)}
                            icon={Calendar}
                            required
                        />
                        <Input
                            label="إلى تاريخ"
                            type="date"
                            value={printDateTo}
                            onChange={e => setPrintDateTo(e.target.value)}
                            icon={Calendar}
                            required
                        />
                    </div>
                    <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                        <Button
                            variant="outline"
                            onClick={() => setIsPrintModalOpen(false)}
                            className="flex-1"
                        >
                            إلغاء
                        </Button>
                        <Button
                            onClick={handlePrintReport}
                            isLoading={printLoading}
                            disabled={!printDateFrom || !printDateTo}
                            className="flex-1 shadow-lg shadow-primary/20"
                        >
                            <Printer className="w-4 h-4 ml-2" />
                            طباعة
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Stats Breakdown Modal */}
            <Modal
                isOpen={isStatsModalOpen}
                onClose={() => setIsStatsModalOpen(false)}
                title="تفاصيل إجمالي المداخيل"
                size="md"
            >
                <div className="space-y-4 pt-2 text-right">
                    <div className="grid grid-cols-1 gap-3">
                        <div 
                            onClick={handleOpenBreakdownModal}
                            className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between cursor-pointer hover:bg-slate-100 hover:border-primary/40 hover:shadow-sm transition-all group"
                            title="انقر لعرض تفاصيل المداخيل حسب الأشهر والانخراط السنوي"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors shadow-sm">
                                    <Wallet size={20} />
                                </div>
                                <div className="text-right">
                                    <div className="flex items-center gap-1.5">
                                        <p className="text-xs text-slate-500 mb-0.5">المجموع الكلي للمحصلات</p>
                                        <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded-full font-medium group-hover:bg-primary group-hover:text-white transition-colors">
                                            عرض التفاصيل
                                        </span>
                                    </div>
                                    <p className={`text-xl font-bold text-slate-800 ${loading ? 'animate-pulse opacity-50' : ''}`}>
                                        {Number(totalCollected || 0).toLocaleString()} د.م
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 text-slate-400 group-hover:text-primary transition-colors">
                                <Badge variant="primary">{activeMonths.length > 0 ? 'مفلتر' : 'إجمالي'}</Badge>
                                <ArrowUpRight className="w-4 h-4 mr-0.5 group-hover:scale-110 transition-transform" />
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-white border border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                                    <AlertCircle size={20} />
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-500 mb-0.5">مجموع لم يودع بعد</p>
                                    <p className={`text-xl text-slate-800 ${loading ? 'animate-pulse opacity-50' : ''}`}>
                                        {Number(totalPending || 0).toLocaleString()} د.م
                                    </p>
                                </div>
                            </div>
                            <Badge variant="warning">قيد الانتظار</Badge>
                        </div>

                        <div className="p-4 rounded-xl bg-white border border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                    <BadgeCheckIcon size={20} />
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-500 mb-0.5">مجموع تم الإيداع البنكي</p>
                                    <p className={`text-xl text-slate-800 ${loading ? 'animate-pulse opacity-50' : ''}`}>
                                        {Number(totalBankDeposited || 0).toLocaleString()} د.م
                                    </p>
                                </div>
                            </div>
                            <Badge variant="success">تم الإيداع</Badge>
                        </div>
                    </div>

                    <div className="pt-2 space-y-3">
                        <div className="border-t border-slate-100 pt-3">
                            <label className="text-[11px] text-slate-400 uppercase font-bold mb-2 block">تصفية حسب الأشهر:</label>
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
                                    'يناير', 'فبراير', 'مارس', 'أبريل',
                                    'مايو', 'يونيو', 'يوليو', 'أغسطس'
                                ].map(month => (
                                    <label 
                                        key={month} 
                                        className={`flex items-center gap-2 p-2 rounded-lg border transition-all cursor-pointer ${
                                            activeMonths.includes(month)
                                            ? 'bg-primary/5 border-primary/20 text-primary'
                                            : 'bg-white border-slate-100 text-slate-500 hover:border-slate-200'
                                        }`}
                                    >
                                        <input 
                                            type="checkbox"
                                            className="w-3 h-3 rounded text-primary focus:ring-primary/20"
                                            checked={activeMonths.includes(month)}
                                            onChange={(e) => {
                                                let updated;
                                                if (e.target.checked) {
                                                    updated = Array.from(new Set([...activeMonths, month]));
                                                } else {
                                                    updated = activeMonths.filter(m => m !== month);
                                                }
                                                setStatsMonths(updated);
                                                if (updated.length === 1) {
                                                    setSelectedMonth(updated[0]);
                                                } else {
                                                    setSelectedMonth('');
                                                }
                                                setCurrentPage(1);
                                            }}
                                        />
                                        <span className="text-[10px] whitespace-nowrap">{month}</span>
                                    </label>
                                ))}
                            </div>
                            {activeMonths.length > 0 && (
                                <button 
                                    onClick={() => { setStatsMonths([]); setSelectedMonth(''); setCurrentPage(1); }}
                                    className="text-[10px] text-primary hover:underline mt-2 block"
                                >
                                    إعادة تعيين الأشهر
                                </button>
                            )}
                        </div>

                        <Button 
                            variant="outline" 
                            onClick={() => setIsStatsModalOpen(false)}
                            className="w-full"
                        >
                            إغلاق
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Revenue Breakdown Modal (Annual Fee & Monthly Collections) */}
            <Modal
                isOpen={isBreakdownModalOpen}
                onClose={() => setIsBreakdownModalOpen(false)}
                title="تفاصيل مجموع المحصلات (الانخراط والأشهر)"
                size="lg"
            >
                {breakdownLoading ? (
                    <div className="p-10 text-center text-slate-500">
                        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                        <p className="text-sm">جاري تحميل التفاصيل...</p>
                    </div>
                ) : breakdownData ? (
                    <div className="space-y-4 pt-1 text-right">
                        {/* 1. معزول في الأول: إجمالي واجب الانخراط السنوي */}
                        <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-teal-500/10 border-2 border-emerald-500/30 flex items-center justify-between shadow-sm">
                            <div className="flex items-center gap-3.5">
                                <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-md shadow-emerald-600/20">
                                    <GraduationCap size={24} />
                                </div>
                                <div className="text-right">
                                    <div className="flex items-center gap-2">
                                        <p className="text-xs font-semibold text-emerald-900">إجمالي واجب الانخراط السنوي</p>
                                        <span className="text-[10px] bg-emerald-600 text-white px-2 py-0.5 rounded-full font-bold">
                                            معزول
                                        </span>
                                    </div>
                                    <p className="text-2xl font-black text-emerald-700 mt-0.5">
                                        {Number(breakdownData.total_registration_fees || 0).toLocaleString()} <span className="text-sm font-semibold">د.م</span>
                                    </p>
                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                        عدد التلاميذ المؤدين للانخراط: {breakdownData.total_registration_count || 0} تلميذ
                                    </p>
                                </div>
                            </div>
                            <div className="text-left hidden sm:block">
                                <Badge variant="success">الانخراط السنوي</Badge>
                            </div>
                        </div>

                        {/* 2. مجموع المدفوعات حسب الأشهر */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between px-1">
                                <span className="text-xs font-bold text-slate-700 uppercase">مجموع المدفوعات حسب الأشهر:</span>
                                <span className="text-xs text-slate-500 font-medium">
                                    مجموع الأشهر: <span className="text-primary font-bold">{Number(breakdownData.total_months_amount || 0).toLocaleString()} د.م</span>
                                </span>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5 max-h-[300px] overflow-y-auto p-1 custom-scrollbar">
                                {breakdownData.months_breakdown?.map((m) => {
                                    const amount = Number(m.amount) || 0;
                                    const hasAmount = amount > 0;
                                    return (
                                        <div
                                            key={m.month}
                                            className={`p-3 rounded-xl border transition-all flex flex-col justify-between ${
                                                hasAmount 
                                                    ? 'bg-white border-slate-200 hover:border-primary/40 hover:shadow-sm' 
                                                    : 'bg-slate-50/70 border-slate-100 opacity-60'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-bold text-slate-700">{m.month}</span>
                                                <Calendar size={13} className={hasAmount ? "text-primary" : "text-slate-400"} />
                                            </div>
                                            <div className="mt-1">
                                                <p className={`text-base font-extrabold ${hasAmount ? 'text-slate-800' : 'text-slate-400'}`}>
                                                    {amount.toLocaleString()} <span className="text-[10px] font-normal text-slate-500">د.م</span>
                                                </p>
                                                {m.count > 0 && (
                                                    <p className="text-[10px] text-slate-400">
                                                        {m.count} عملية أداء
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 3. شريط الخلاصة والمجموع الكلي للمحصلات */}
                        <div className="p-4 rounded-xl bg-slate-900 text-white flex flex-col sm:flex-row items-center justify-between gap-3 shadow-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-amber-400">
                                    <Wallet size={20} />
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-300 font-medium">المجموع الكلي للمحصلات</p>
                                    <p className="text-[11px] text-slate-400">
                                        إجمالي الانخراط ({Number(breakdownData.total_registration_fees || 0).toLocaleString()} د.م) + إجمالي الأشهر ({Number(breakdownData.total_months_amount || 0).toLocaleString()} د.م)
                                    </p>
                                </div>
                            </div>
                            <div className="text-left">
                                <p className="text-2xl font-black text-amber-400">
                                    {Number(breakdownData.grand_total || 0).toLocaleString()} <span className="text-sm font-bold text-white">د.م</span>
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end pt-2">
                            <Button
                                variant="outline"
                                onClick={() => setIsBreakdownModalOpen(false)}
                                className="w-full sm:w-auto"
                            >
                                إغلاق
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Modal>

            {/* Unpaid Students Filter Modal */}
            <Modal
                isOpen={isUnpaidModalOpen}
                onClose={() => setIsUnpaidModalOpen(false)}
                title="استخراج لائحة غير المسددين"
                size="md"
            >
                <UnpaidFilterModal 
                    academicYears={academicYears}
                    onClose={() => setIsUnpaidModalOpen(false)} 
                />
            </Modal>

            {/* Beneficiary Statistics Filter Modal */}
            <Modal
                isOpen={isBeneficiaryStatsModalOpen}
                onClose={() => setIsBeneficiaryStatsModalOpen(false)}
                title="فلاتر تقرير إحصائيات المستفيدين"
                size="md"
            >
                <BeneficiaryStatsFilterModal 
                    academicYears={academicYears}
                    onClose={() => setIsBeneficiaryStatsModalOpen(false)} 
                />
            </Modal>
        </div>
    );
};

const BeneficiaryStatsFilterModal = ({ academicYears, onClose }) => {
    const [academicYearId, setAcademicYearId] = useState(academicYears?.find(y => y.is_active)?.id || '');
    const [month, setMonth] = useState('');
    const [regionId, setRegionId] = useState('');
    const [institutionId, setInstitutionId] = useState('');
    const [schoolClassId, setSchoolClassId] = useState('');
    const [level, setLevel] = useState('');
    const [busId, setBusId] = useState('');
    const [gender, setGender] = useState('');
    const [groupId, setGroupId] = useState('');
    const [isActive, setIsActive] = useState('');

    const { data: regionsRaw } = useGet('/regions?all=true');
    const { data: institutionsRaw } = useGet('/institutions');
    const { data: classesRaw } = useGet('/school-classes');
    const { data: busesRaw } = useGet('/buses');
    const { data: groupsRaw } = useGet('/groups');

    const regions = Array.isArray(regionsRaw) ? regionsRaw : (regionsRaw?.data || []);
    const institutions = Array.isArray(institutionsRaw) ? institutionsRaw : (institutionsRaw?.data || []);
    const schoolClasses = Array.isArray(classesRaw) ? classesRaw : (classesRaw?.data || []);
    const buses = Array.isArray(busesRaw) ? busesRaw : (busesRaw?.data || []);
    const groups = Array.isArray(groupsRaw) ? groupsRaw : (groupsRaw?.data || []);

    const handlePrint = () => {
        const filters = {
            academic_year_id: academicYearId,
            month: month,
            region_id: regionId,
            institution_id: institutionId,
            school_class_id: schoolClassId,
            level: level,
            bus_id: busId,
            gender: gender,
            group_id: groupId,
            is_active: isActive,
        };
        localStorage.setItem('beneficiaries_stats_filters', JSON.stringify(filters));
        window.open('/print/beneficiaries-stats', '_blank');
        onClose();
    };

    return (
        <div className="space-y-4 text-right" dir="rtl">
            <p className="text-xs text-slate-500 mb-4">
                اختر الفلاتر المناسبة لاستخراج تقرير إحصائيات المستفيدين التفصيلي حسب كل دوار وسلك تعليمي.
            </p>

            <div className="space-y-3">
                <Select
                    label="السنة الدراسية"
                    options={[
                        { value: '', label: 'جميع السنوات الدراسية' },
                        ...(academicYears?.map(y => ({ value: y.id, label: y.name })) || [])
                    ]}
                    value={academicYearId}
                    onChange={(e) => setAcademicYearId(e.target.value)}
                />

                <Select
                    label="الشهر (تحديد المؤدين لشهر معين)"
                    options={[
                        { value: '', label: 'جميع الأشهر' },
                        { value: 'سبتمبر', label: 'سبتمبر' },
                        { value: 'أكتوبر', label: 'أكتوبر' },
                        { value: 'نوفمبر', label: 'نوفمبر' },
                        { value: 'ديسمبر', label: 'ديسمبر' },
                        { value: 'يناير', label: 'يناير' },
                        { value: 'فبراير', label: 'فبراير' },
                        { value: 'مارس', label: 'مارس' },
                        { value: 'أبريل', label: 'أبريل' },
                        { value: 'مايو', label: 'مايو' },
                        { value: 'يونيو', label: 'يونيو' },
                        { value: 'يوليو', label: 'يوليو' },
                        { value: 'أغسطس', label: 'أغسطس' },
                    ]}
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                />

                <Select
                    label="المنطقة / الدوار"
                    options={[
                        { value: '', label: 'جميع المناطق والدوارات' },
                        ...(regions?.map(r => ({ value: r.id, label: r.region_name })) || [])
                    ]}
                    value={regionId}
                    onChange={(e) => setRegionId(e.target.value)}
                />

                <Select
                    label="المؤسسة التعليمية"
                    options={[
                        { value: '', label: 'جميع المؤسسات' },
                        ...(institutions?.map(i => ({ value: i.id, label: i.name })) || [])
                    ]}
                    value={institutionId}
                    onChange={(e) => setInstitutionId(e.target.value)}
                />

                <Select
                    label="المستوى الدراسي"
                    options={[
                        { value: '', label: 'جميع المستويات' },
                        { value: 'إعدادي', label: 'التعليم الإعدادي (جميع مستويات الإعدادي)' },
                        { value: 'ثانوي', label: 'التعليم الثانوي (جميع مستويات الثانوي)' },
                        { value: 'أولى إعدادي', label: 'أولى إعدادي' },
                        { value: 'ثانية إعدادي', label: 'ثانية إعدادي' },
                        { value: 'ثالثة إعدادي', label: 'ثالثة إعدادي' },
                        { value: 'جذع مشترك', label: 'جذع مشترك' },
                        { value: 'أولى باك', label: 'أولى باك' },
                        { value: 'ثانية باك', label: 'ثانية باك' },
                    ]}
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                />

                <Select
                    label="القسم"
                    options={[
                        { value: '', label: 'جميع الأقسام' },
                        ...(schoolClasses?.map(c => ({ value: c.id, label: c.name })) || [])
                    ]}
                    value={schoolClassId}
                    onChange={(e) => setSchoolClassId(e.target.value)}
                />

                <Select
                    label="الحافلة / الخط"
                    options={[
                        { value: '', label: 'جميع الحافلات' },
                        ...(buses?.map(b => ({ value: b.id, label: b.name || `حافلة #${b.id}` })) || [])
                    ]}
                    value={busId}
                    onChange={(e) => setBusId(e.target.value)}
                />

                <Select
                    label="الجنس"
                    options={[
                        { value: '', label: 'جميع الفئات (ذكور وإناث)' },
                        { value: 'Male', label: 'ذكور' },
                        { value: 'Female', label: 'إناث' }
                    ]}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                />

                {groups.length > 0 && (
                    <Select
                        label="المجموعة"
                        options={[
                            { value: '', label: 'جميع المجموعات' },
                            ...(groups?.map(g => ({ value: g.id, label: g.name })) || [])
                        ]}
                        value={groupId}
                        onChange={(e) => setGroupId(e.target.value)}
                    />
                )}

                <Select
                    label="حالة الطالب"
                    options={[
                        { value: '', label: 'جميع الحالات (نشيط وغير نشيط)' },
                        { value: '1', label: 'المستفيدون النشيطون فقط' },
                        { value: '0', label: 'غير النشيطين / المنقطعين فقط' }
                    ]}
                    value={isActive}
                    onChange={(e) => setIsActive(e.target.value)}
                />
            </div>

            <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3 mt-6">
                <Button variant="outline" onClick={onClose}>
                    إلغاء
                </Button>
                <Button onClick={handlePrint} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Printer className="w-4 h-4 ml-2" />
                    طباعة التقرير
                </Button>
            </div>
        </div>
    );
};


const UnpaidFilterModal = ({ academicYears, onClose }) => {
    const [activeTab, setActiveTab] = useState('unpaid'); // 'unpaid' or 'status'
    const [selectedMonths, setSelectedMonths] = useState([]);
    const [academicYearId, setAcademicYearId] = useState(academicYears?.find(y => y.is_active)?.id || '');
    const [regionId, setRegionId] = useState('');
    const [institutionId, setInstitutionId] = useState('');
    const { data: regionsRaw } = useGet('/regions?all=true');
    const regions = Array.isArray(regionsRaw) ? regionsRaw : (regionsRaw?.data || []);
    const { data: institutionsRaw } = useGet('/institutions');
    const institutions = Array.isArray(institutionsRaw) ? institutionsRaw : (institutionsRaw?.data || []);

    const monthsList = [
        { id: '09', name: 'سبتمبر' }, { id: '10', name: 'أكتوبر' }, { id: '11', name: 'نوفمبر' }, { id: '12', name: 'ديسمبر' },
        { id: '01', name: 'يناير' }, { id: '02', name: 'فبراير' }, { id: '03', name: 'مارس' }, { id: '04', name: 'أبريل' },
        { id: '05', name: 'مايو' }, { id: '06', name: 'يونيو' }, { id: '07', name: 'يوليو' }, { id: '08', name: 'أغسطس' }
    ];

    const toggleMonth = (name) => {
        setSelectedMonths(prev => 
            prev.includes(name) ? prev.filter(m => m !== name) : [...prev, name]
        );
    };

    const handlePrintUnpaid = () => {
        if (!academicYearId) return;
        if (selectedMonths.length === 0) return;

        const params = new URLSearchParams();
        params.append('academic_year_id', academicYearId);
        selectedMonths.forEach(m => params.append('months[]', m));
        if (regionId) params.append('region_id', regionId);
        if (institutionId) params.append('institution_id', institutionId);

        window.open(`/print/unpaid-students?${params.toString()}`, '_blank');
        onClose();
    };

    const handlePrintStatus = () => {
        if (!academicYearId) return;
        const params = new URLSearchParams();
        params.append('academic_year_id', academicYearId);
        if (regionId) params.append('region_id', regionId);
        if (institutionId) params.append('institution_id', institutionId);

        window.open(`/print/payment-status?${params.toString()}`, '_blank');
        onClose();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-1 p-1 bg-slate-50 rounded-xl border border-slate-100">
                <button
                    onClick={() => setActiveTab('unpaid')}
                    className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${
                        activeTab === 'unpaid'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    لائحة الغير المسددين
                </button>
                <button
                    onClick={() => setActiveTab('status')}
                    className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all ${
                        activeTab === 'status'
                        ? 'bg-white text-primary shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                    حالة المدفوعات
                </button>
            </div>

            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select 
                        label="السنة الدراسية" 
                        options={[
                            { value: '', label: 'اختر السنة' },
                            ...(academicYears?.map(ay => ({ value: ay.id, label: ay.name })) || [])
                        ]}
                        value={academicYearId}
                        onChange={(e) => setAcademicYearId(e.target.value)}
                    />
                    <Select
                        label={<span>المنطقة <span className="text-slate-400 text-[10px] font-normal">(اختياري)</span></span>}
                        options={[
                            { value: '', label: 'جميع المناطق' },
                            ...(regions?.map(r => ({ value: r.id, label: r.region_name || r.name })) || [])
                        ]}
                        value={regionId}
                        onChange={(e) => setRegionId(e.target.value)}
                    />
                    <Select
                        label={<span>المؤسسة <span className="text-slate-400 text-[10px] font-normal">(اختياري)</span></span>}
                        options={[
                            { value: '', label: 'جميع المؤسسات' },
                            ...(institutions?.map(i => ({ value: i.id, label: i.name })) || [])
                        ]}
                        value={institutionId}
                        onChange={(e) => setInstitutionId(e.target.value)}
                    />
                </div>

                {activeTab === 'unpaid' && (
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700 block">اختر الأشهر المراد التحقق منها:</label>
                        <div className="grid grid-cols-3 gap-2">
                            {monthsList.map(month => (
                                <button
                                    key={month.id}
                                    type="button"
                                    onClick={() => toggleMonth(month.name)}
                                    className={`py-2 px-1 text-[11px] rounded-lg border transition-all ${
                                        selectedMonths.includes(month.name)
                                        ? 'bg-primary text-white border-primary shadow-sm'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-primary/40 hover:bg-primary/5'
                                    }`}
                                >
                                    {month.name}
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-start gap-2 pt-1">
                            <button 
                                type="button" 
                                onClick={() => setSelectedMonths(monthsList.map(m => m.name))}
                                className="text-[10px] text-primary hover:underline"
                            >
                                تحديد الكل
                            </button>
                            <button 
                                type="button" 
                                onClick={() => setSelectedMonths([])}
                                className="text-[10px] text-slate-400 hover:underline"
                            >
                                إلغاء التحديد
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                <Button 
                    className="flex-1" 
                    disabled={!academicYearId || (activeTab === 'unpaid' && selectedMonths.length === 0)}
                    onClick={activeTab === 'unpaid' ? handlePrintUnpaid : handlePrintStatus}
                >
                    <Printer className="w-4 h-4 ml-2" />
                    {activeTab === 'unpaid' ? 'استخراج اللائحة' : 'استخراج حالة المدفوعات'}
                </Button>
                <Button variant="outline" onClick={onClose} className="flex-1">
                    إلغاء
                </Button>
            </div>
        </div>
    );
};

const PaymentForm = ({ years, modes, payment, onSuccess }) => {
    const { selectedYear } = useAuth();

    const [formData, setFormData] = useState(payment || {
        student_id: '',
        academic_year_id: selectedYear?.id || '',
        amount: '0',
        months: [],
        is_registration_fee: false,
        payment_mode_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        cheque_number: payment?.cheque_number || ''
    });

    const [selectedStudent, setSelectedStudent] = useState(null);
    const [monthlyPrice, setMonthlyPrice] = useState(
        payment 
            ? ((parseFloat(payment.amount || 0) - (payment.is_registration_fee ? parseFloat(payment.student?.registration_fee || 0) : 0)) / ((payment.months?.length || 1) || 1)).toString()
            : ''
    );
    const [loadingStudent, setLoadingStudent] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchStudent, setSearchStudent] = useState('');
    const [searchingStudent, setSearchingStudent] = useState(false);
    const [studentSearchError, setStudentSearchError] = useState('');

    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const searchTimeout = React.useRef(null);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchStudent(val);
        
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        
        if (!val.trim()) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        searchTimeout.current = setTimeout(async () => {
            setSearchingStudent(true);
            try {
                const res = await api.get(`/students?all=true&search=${val}`);
                const data = res.data?.data || res.data || [];
                setSearchResults(data);
                setShowDropdown(true);
            } catch (err) {
                console.error(err);
            } finally {
                setSearchingStudent(false);
            }
        }, 300);
    };

    const selectStudent = (student) => {
        setFormData(prev => ({ ...prev, student_id: student.id }));
        setSearchStudent('');
        setShowDropdown(false);
        setSearchResults([]);
    };

    const handleKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (searchResults.length > 0) {
                const found = searchResults.find(s => s.qr_code === searchStudent || s.code_massar === searchStudent) || searchResults[0];
                selectStudent(found);
            } else if (searchStudent.trim()) {
                setSearchingStudent(true);
                try {
                    const res = await api.get(`/students?all=true&search=${searchStudent}`);
                    const data = res.data?.data || res.data || [];
                    if (data.length > 0) {
                        const found = data.find(s => s.qr_code === searchStudent || s.code_massar === searchStudent) || data[0];
                        selectStudent(found);
                    } else {
                        setShowDropdown(true);
                        setSearchResults([]);
                    }
                } catch (err) {} finally { setSearchingStudent(false); }
            }
        }
    };

    // Auto-select ESPECE when adding a new payment
    useEffect(() => {
        if (!payment && modes?.length > 0 && !formData.payment_mode_id) {
            const especeMode = modes.find(m => m.name?.toUpperCase().includes('ESPECE') || m.name?.toUpperCase().includes('ESPÈCE'));
            if (especeMode) {
                setFormData(prev => ({ ...prev, payment_mode_id: especeMode.id }));
            }
        }
    }, [modes]);

    const monthsList = [
        { id: '09', name: 'سبتمبر' }, { id: '10', name: 'أكتوبر' }, { id: '11', name: 'نوفمبر' }, { id: '12', name: 'ديسمبر' },
        { id: '01', name: 'يناير' }, { id: '02', name: 'فبراير' }, { id: '03', name: 'مارس' }, { id: '04', name: 'أبريل' },
        { id: '05', name: 'مايو' }, { id: '06', name: 'يونيو' }, { id: '07', name: 'يوليو' }, { id: '08', name: 'أغسطس' }
    ];

    // Fetch full student details when student_id changes
    useEffect(() => {
        const fetchStudent = async () => {
            if (!formData.student_id) {
                setSelectedStudent(null);
                return;
            }
            setLoadingStudent(true);
            try {
                const res = await api.get(`/students/${formData.student_id}`);
                const studentData = res.data.data;
                setSelectedStudent(studentData);
                
                // If adding new, auto-set monthly price and academic year
                if (!payment) {
                    const studentMonthly = studentData.subscription_price !== undefined ? String(studentData.subscription_price) : '0';
                    setMonthlyPrice(studentMonthly);
                    setFormData(prev => {
                        const mPrice = parseFloat(studentMonthly || 0);
                        const rPrice = parseFloat(studentData.registration_fee || 0);
                        const total = ((prev.months?.length || 0) * mPrice) + (prev.is_registration_fee ? rPrice : 0);
                        return {
                            ...prev,
                            academic_year_id: selectedYear?.id || studentData.academic_year_id || prev.academic_year_id,
                            amount: total.toString()
                        };
                    });
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingStudent(false);
            }
        };
        fetchStudent();
    }, [formData.student_id]);

    const handleMonthlyPriceChange = (val) => {
        setMonthlyPrice(val);
        const mPrice = parseFloat(val || 0);
        const rPrice = parseFloat(selectedStudent?.registration_fee || 0);
        const total = ((formData.months?.length || 0) * mPrice) + (formData.is_registration_fee ? rPrice : 0);
        setFormData(prev => ({ ...prev, amount: total.toString() }));
    };

    const toggleRegistrationFee = () => {
        if (!selectedStudent) return;
        setFormData(prev => {
            const newValue = !prev.is_registration_fee;
            const mPrice = parseFloat(monthlyPrice || selectedStudent.subscription_price || 0);
            const rPrice = parseFloat(selectedStudent.registration_fee || 0);
            const monthCount = (prev.months || []).length;
            const newAmount = (monthCount * mPrice) + (newValue ? rPrice : 0);
            return { ...prev, is_registration_fee: newValue, amount: newAmount.toString() };
        });
    };

    const toggleMonth = (monthName) => {
        if (!selectedStudent) return;
        setFormData(prev => {
            const currentMonths = prev.months || [];
            let newMonths = currentMonths.includes(monthName)
                ? currentMonths.filter(m => m !== monthName)
                : [...currentMonths, monthName];
            
            const mPrice = parseFloat(monthlyPrice || selectedStudent.subscription_price || 0);
            const rPrice = parseFloat(selectedStudent.registration_fee || 0);
            const newAmount = (newMonths.length * mPrice) + (prev.is_registration_fee ? rPrice : 0);
            
            return { ...prev, months: newMonths, amount: newAmount.toString() };
        });
    };

    const { success, error: toastError } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate date against selected year
        if (selectedYear && formData.payment_date) {
            const date = formData.payment_date;
            if (date < selectedYear.start_date || date > selectedYear.end_date) {
                toastError(`تاريخ الدفع يجب أن يكون بين ${selectedYear.start_date} و ${selectedYear.end_date}`);
                return;
            }
        }

        setSubmitting(true);
        try {
            if (payment) {
                await api.put(`/payments/${payment.id}`, formData);
                success('تم تحديث بيانات الدفعة بنجاح');
            } else {
                await api.post('/payments', formData);
                success('تم تسجيل عملية الدفع بنجاح');
            }
            onSuccess();
        } catch (err) {
            toastError(err.response?.data?.message || 'خطأ أثناء الحفظ');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!payment) return;
        if (window.confirm('هل أنت متأكد من حذف هذه العملية؟')) {
            setSubmitting(true);
            try {
                await api.delete(`/payments/${payment.id}`);
                success('تم حذف الدفعة بنجاح');
                onSuccess();
            } catch (err) {
                toastError(err.response?.data?.message || 'خطأ أثناء الحفظ');
            } finally {
                setSubmitting(false);
            }
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-1">
            {/* Left Column: Student Info (Matching QuickPayment) */}
            <div className="md:col-span-1 space-y-4">
                <Card className="p-4 bg-slate-50/50 border-slate-100 shadow-none h-full">
                    <div className="flex flex-col items-center text-center space-y-3">
                        <div className="w-16 h-16 rounded-2xl bg-white text-primary flex items-center justify-center border border-slate-100 shadow-sm">
                            <User size={32} strokeWidth={1.5} />
                        </div>
                        <div className="w-full text-right">
                            {!payment ? (
                                <div className="relative w-full text-right">
                                    <label className="block text-sm font-medium text-slate-700 mb-1">ابحث عن الطالب</label>
                                    <div className="relative flex items-center bg-white border border-slate-200 rounded-xl h-11 focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all shadow-sm z-40">
                                        <div className="flex-1 flex items-center pr-3 min-w-0 h-full">
                                            <Search size={16} className="text-slate-400 shrink-0 ml-2" />
                                            <input 
                                                placeholder="الاسم، مسار، أو رمز QR..."
                                                value={searchStudent}
                                                onChange={handleSearchChange}
                                                onKeyDown={handleKeyDown}
                                                onFocus={() => { if(searchStudent.trim()) setShowDropdown(true); }}
                                                className="w-full h-full py-2 outline-none text-sm bg-transparent"
                                            />
                                        </div>
                                        <div className="h-full px-4 bg-slate-50 text-slate-400 text-xs  transition-colors flex items-center justify-center gap-1 border-r border-slate-200 rounded-l-xl">
                                            {searchingStudent ? <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div> : <Search size={14} />}
                                        </div>
                                    </div>
                                    {showDropdown && searchStudent.trim() && (
                                        <div className="absolute top-full right-0 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto divide-y divide-slate-100">
                                            {searchingStudent && searchResults.length === 0 ? (
                                                <div className="p-4 text-center text-xs text-slate-500">جاري البحث...</div>
                                            ) : searchResults.length === 0 ? (
                                                <div className="p-4 text-center text-xs text-slate-500">لم يتم العثور على أي طالب</div>
                                            ) : (
                                                searchResults.map(s => (
                                                    <div 
                                                        key={s.id} 
                                                        onClick={() => selectStudent(s)}
                                                        className="p-3 hover:bg-slate-50 cursor-pointer transition-colors text-right flex items-center justify-between"
                                                    >
                                                        <div>
                                                            <div className="text-sm  text-slate-800">{s.full_name}</div>
                                                            <div className="text-[10px] text-slate-500">{s.code_massar} • {s.school_class?.name || 'بدون قسم'}</div>
                                                        </div>
                                                        {s.qr_code && (
                                                            <div className="text-[10px] bg-slate-100 px-2 py-1 rounded text-slate-500 font-mono" dir="ltr">
                                                                {s.qr_code}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                    {/* Selected Student Name Feedback */}
                                    {selectedStudent && !searchStudent.trim() && (
                                        <div className="mt-2 animate-in fade-in slide-in-from-top-1">
                                            <Badge variant="" className="px-3 py-1 flex items-center justify-center gap-2 w-full text-xs">
                                                <User size={12} />
                                                تم اختيار الطالب: {selectedStudent.full_name}
                                            </Badge>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="w-full text-right">
                                    <label className="block text-sm font-medium text-slate-700 mb-1">الطالب (لا يمكن تعديله)</label>
                                    <div className="h-11 px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 text-sm flex items-center ">
                                        {selectedStudent?.full_name || '...'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {loadingStudent ? (
                        <div className="py-8 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full"></div></div>
                    ) : selectedStudent ? (
                        <div className="mt-6 space-y-3 animate-in fade-in duration-500">
                            <div className="p-3 rounded-xl bg-white border border-slate-100 flex items-center gap-3">
                                <GraduationCap className="w-4 h-4 text-slate-400" />
                                <div className="flex flex-col">
                                    <span className="text-[9px] text-slate-400 uppercase">المدرسة</span>
                                    <span className="text-xs text-slate-700">{selectedStudent.school || '--'} - {selectedStudent.school_class?.name || '--'}</span>
                                </div>
                            </div>
                            <div className="p-3 rounded-xl bg-white border border-slate-100 flex items-center gap-3">
                                <BusIcon className="w-4 h-4 text-slate-400" />
                                <div className="flex flex-col">
                                    <span className="text-[9px] text-slate-400 uppercase">الحافلة</span>
                                    <span className="text-xs text-slate-700">{selectedStudent.bus?.matricule || selectedStudent.bus?.name || '--'}</span>
                                </div>
                            </div>
                            <div className="p-3 rounded-xl bg-white border border-slate-100 flex items-center gap-3">
                                <MapPin className="w-4 h-4 text-slate-400" />
                                <div className="flex flex-col">
                                    <span className="text-[9px] text-slate-400 uppercase">المنطقة / الدوار</span>
                                    <span className="text-xs text-slate-700">
                                        {selectedStudent.region?.region_name || '--'}
                                        {selectedStudent.place?.place_name ? ` - ${selectedStudent.place.place_name}` : ''}
                                    </span>
                                </div>
                            </div>
                            <div className="p-3 rounded-xl bg-primary/5 border border-primary/10 flex items-center gap-3 text-primary">
                                <Wallet className="w-4 h-4" />
                                <div className="flex flex-col">
                                    <span className="text-[9px] opacity-70 uppercase tracking-tighter">الاشتراك الشهري</span>
                                    <span className="text-sm font-semibold">{selectedStudent.subscription_price || 0} د.م</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="py-12 text-center text-slate-300 text-xs italic">يرجى اختيار طالب لعرض المعلومات</div>
                    )}
                </Card>
            </div>

            {/* Right Column: Form (Matching QuickPayment) */}
            <div className="md:col-span-2">
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Registration Fee */}
                    {selectedStudent && selectedStudent.registration_fee > 0 && (
                        <button
                            type="button"
                            disabled={selectedStudent.has_paid_registration_fee && !formData.is_registration_fee}
                            onClick={toggleRegistrationFee}
                            className={`w-full p-4 rounded-2xl border transition-all relative overflow-hidden text-right ${
                                selectedStudent.has_paid_registration_fee && !formData.is_registration_fee
                                ? 'bg-slate-50 border-slate-100 opacity-60'
                                : formData.is_registration_fee
                                    ? 'bg-emerald-50 border-emerald-200 shadow-sm ring-1 ring-emerald-100'
                                    : 'bg-slate-50/50 border-slate-100 hover:bg-white hover:border-slate-200'
                            }`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                                        formData.is_registration_fee ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                                    }`}>
                                        <BadgeCheckIcon className="w-5 h-5" />
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-semibold text-slate-800 leading-none mb-1">واجب الانخراط السنوي</p>
                                        <p className="text-[10px] text-slate-400">يدفع مرة واحدة كل موسم</p>
                                    </div>
                                </div>
                                <span className="text-sm  text-slate-800">{selectedStudent.registration_fee} د.م</span>
                            </div>
                            {selectedStudent.has_paid_registration_fee && !formData.is_registration_fee && (
                                <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
                                    <Badge variant="success" className="text-[10px] py-0.5">تم تسوية الوضعية مسبقاً</Badge>
                                </div>
                            )}
                        </button>
                    )}

                    {/* Months Selection */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-slate-500">اختر الأشهر:</label>
                            {selectedStudent?.paid_months?.length > 0 && (
                                <div className="flex items-center gap-1.5 animate-in fade-in duration-500">
                                    <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100 flex items-center gap-1">
                                        <BadgeCheck size={10} />
                                        الأشهر المستوفاة: {selectedStudent.paid_months.join('، ')}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                            {monthsList.map(month => {
                                const isSelected = formData.months?.includes(month.name);
                                // If payment (edit mode), show all months as requested.
                                // If !payment (create mode), disable already paid months.
                                const isAlreadyPaid = !payment && selectedStudent?.paid_months?.includes(month.name);
                                
                                return (
                                    <button
                                        key={month.id}
                                        type="button"
                                        onClick={() => !isAlreadyPaid && toggleMonth(month.name)}
                                        disabled={!selectedStudent || isAlreadyPaid}
                                        className={`relative py-2.5 text-[10px] rounded-xl border transition-all duration-200 ${
                                            isAlreadyPaid
                                            ? 'bg-emerald-50/50 text-emerald-400 border-emerald-100 cursor-not-allowed opacity-80'
                                            : isSelected 
                                                ? 'bg-primary text-white border-primary shadow-md shadow-primary/10 transform scale-105 z-10' 
                                                : 'bg-white text-slate-500 border-slate-100 hover:border-slate-300'
                                        }`}
                                    >
                                        {month.name}
                                        {isAlreadyPaid && (
                                            <div className="absolute -top-1 -right-1 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white shadow-sm">
                                                <BadgeCheck size={8} />
                                            </div>
                                        )}
                                        {payment && selectedStudent?.paid_months?.includes(month.name) && !isSelected && (
                                            <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[7px] px-1 rounded-full border border-white">مدفوع</span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Financial Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Select 
                            label="السنة الدراسية" 
                            options={[{ value: '', label: 'اختر السنة' }, ...(years?.map(y => ({ value: y.id, label: y.name })) || [])]} 
                            value={formData.academic_year_id} 
                            onChange={e => setFormData({...formData, academic_year_id: e.target.value})} 
                            required
                            disabled
                            className="bg-slate-50/50 border-slate-200"
                        />
                        <Input 
                            label="واجب الاشتراك الشهري (درهم)" 
                            type="number" 
                            value={monthlyPrice} 
                            onChange={e => handleMonthlyPriceChange(e.target.value)} 
                            placeholder="0"
                            icon={Wallet}
                            required
                            className="bg-white border-slate-200 font-bold text-slate-800"
                        />
                        <Input 
                            label="المبلغ الإجمالي المؤدى (درهم)" 
                            type="number" 
                            value={formData.amount} 
                            onChange={e => {
                                const newTotal = e.target.value;
                                setFormData(prev => ({ ...prev, amount: newTotal }));
                                const mCount = formData.months?.length || 1;
                                const rPrice = formData.is_registration_fee ? parseFloat(selectedStudent?.registration_fee || 0) : 0;
                                const subTotal = Math.max(0, parseFloat(newTotal || 0) - rPrice);
                                setMonthlyPrice((subTotal / mCount).toFixed(2));
                            }} 
                            required 
                            icon={Wallet}
                            className="bg-slate-50 border-slate-200 font-bold text-primary text-base"
                        />
                        <Select 
                            label="وسيلة الدفع" 
                            options={[{ value: '', label: 'اختر الوسيلة' }, ...(modes?.map(m => ({ value: m.id, label: m.name })) || [])]} 
                            value={formData.payment_mode_id} 
                            onChange={e => setFormData({...formData, payment_mode_id: e.target.value})} 
                            required
                            className="bg-slate-50/50 border-slate-200"
                        />
                        {/* Conditionally show cheque number input if selected mode name contains "CHEQUE" or "شيك" */}
                        {modes?.find(m => m.id == formData.payment_mode_id)?.name?.toUpperCase().includes('CHEQUE') || 
                         modes?.find(m => m.id == formData.payment_mode_id)?.name?.includes('شيك') ? (
                            <Input 
                                label="رقم الشيك"
                                value={formData.cheque_number}
                                onChange={e => setFormData({...formData, cheque_number: e.target.value})}
                                placeholder="أدخل رقم الشيك هنا..."
                                icon={CreditCard}
                                required
                                className="bg-slate-50/50 border-slate-200 animate-in fade-in slide-in-from-top-2"
                            />
                        ) : null}
                        <Input 
                            label="تاريخ الدفع" 
                            type="date" 
                            value={formData.payment_date} 
                            onChange={e => setFormData({...formData, payment_date: e.target.value})} 
                            required 
                            icon={Calendar}
                            className="bg-slate-50/50 border-slate-200"
                        />
                    </div>

                    {/* Live Calculation Summary */}
                    {selectedStudent && (formData.months?.length > 0 || formData.is_registration_fee) && (
                        <div className="p-3 rounded-xl bg-primary/5 border border-primary/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs">
                            <div className="text-slate-600">
                                <span className="text-slate-400 ml-1">تفاصيل الحساب:</span>
                                {formData.months?.length > 0 && (
                                    <span className="font-semibold text-slate-800">
                                        {formData.months.length} أشهر × {monthlyPrice || 0} د.م = {((formData.months.length) * parseFloat(monthlyPrice || 0)).toLocaleString()} د.م
                                    </span>
                                )}
                                {formData.is_registration_fee && (
                                    <span className="font-semibold text-emerald-700">
                                        {formData.months?.length > 0 ? ' + ' : ''}واجب انخراط {selectedStudent.registration_fee} د.م
                                    </span>
                                )}
                            </div>
                            <div className="font-bold text-primary text-sm whitespace-nowrap">
                                المجموع الإجمالي: {parseFloat(formData.amount || 0).toLocaleString()} د.م
                            </div>
                        </div>
                    )}

                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                        {payment && (
                            <Button 
                                type="button" 
                                variant="outline" 
                                onClick={handleDelete}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 border-red-100"
                                disabled={submitting}
                            >
                                <Trash2 size={16} className="ml-2" />
                                حذف الدفعة
                            </Button>
                        )}
                        <Button 
                            type="submit" 
                            isLoading={submitting} 
                            className={`flex-1 shadow-lg shadow-primary/10 h-11 ${payment ? 'bg-slate-800 hover:bg-slate-900' : ''}`}
                            disabled={!formData.student_id || (!formData.months?.length && !formData.is_registration_fee)}
                        >
                            {payment ? 'تحديث المعلومات' : 'تأكيد وتسجيل الدفعة الآن'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PaymentList;
